import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { DateTime } from 'luxon';

import {
  toApiDate,
  todayInWarsaw,
  toWarsawDateTime,
} from 'core/utils/zoomTime';

/**
 * Shared Zoom filter — the URL query string is the single source of truth, so
 * the selected group + date range survive page refresh, are bookmarkable, and
 * (via {@link ZoomTabs} preserving these keys) carry across tab navigation.
 *
 * Only the *shared* keys live here: `from`, `to` (yyyy-mm-dd), `group`,
 * `showDeleted`. Page-private params (`status`, `orphanOnly`, `tooShort`,
 * `lesson`, …) are read/written by the individual pages and are preserved by
 * `pushSearch` (it merges onto the current params).
 *
 * Defaults are computed from "now" and are deliberately NOT written to the URL
 * on mount — writing on mount is the classic react-router replace-loop trap.
 * Every write goes through a single `history.replace` so filter tweaks don't
 * pile up in the browser back-stack.
 */
const DEFAULT_RANGE_DAYS = 6; // last 7 days, inclusive

export interface IZoomFilters {
  dateFrom: DateTime;
  dateTo: DateTime;
  groupId?: string;
  showDeleted: boolean;
}

export interface IZoomFiltersResult {
  filters: IZoomFilters;
  /** yyyy-mm-dd pair ready to pass as GraphQL `Date` args. */
  apiRange: { dateFrom: string; dateTo: string };
  setRange: (from: DateTime, to: DateTime) => void;
  setGroupId: (groupId?: string) => void;
  setShowDeleted: (showDeleted: boolean) => void;
}

const parseDate = (value: unknown, fallback: DateTime): DateTime => {
  if (typeof value === 'string') {
    const dt = toWarsawDateTime(value);
    if (dt) {
      return dt.startOf('day');
    }
  }
  return fallback;
};

export const useZoomFilters = (): IZoomFiltersResult => {
  const location = useLocation();
  const history = useHistory();

  const filters = useMemo<IZoomFilters>(() => {
    const parsed = queryString.parse(location.search);
    const today = todayInWarsaw();
    return {
      dateFrom: parseDate(
        parsed.from,
        today.minus({ days: DEFAULT_RANGE_DAYS }),
      ),
      dateTo: parseDate(parsed.to, today),
      groupId:
        typeof parsed.group === 'string' && parsed.group
          ? parsed.group
          : undefined,
      showDeleted: parsed.showDeleted === 'true',
    };
  }, [location.search]);

  const pushSearch = useCallback(
    (patch: Record<string, string | undefined>) => {
      const next: Record<string, unknown> = {
        ...queryString.parse(location.search),
        ...patch,
      };
      // Drop empty keys so the URL stays clean (and defaults stay implicit).
      Object.keys(next).forEach((key) => {
        const v = next[key];
        if (v === undefined || v === null || v === '') {
          delete next[key];
        }
      });
      history.replace({
        pathname: location.pathname,
        search: queryString.stringify(next),
      });
    },
    [history, location.pathname, location.search],
  );

  const setRange = useCallback(
    (from: DateTime, to: DateTime) =>
      pushSearch({ from: toApiDate(from), to: toApiDate(to) }),
    [pushSearch],
  );

  const setGroupId = useCallback(
    (groupId?: string) => pushSearch({ group: groupId || undefined }),
    [pushSearch],
  );

  const setShowDeleted = useCallback(
    (showDeleted: boolean) =>
      pushSearch({ showDeleted: showDeleted ? 'true' : undefined }),
    [pushSearch],
  );

  const apiRange = useMemo(
    () => ({
      dateFrom: toApiDate(filters.dateFrom),
      dateTo: toApiDate(filters.dateTo),
    }),
    [filters.dateFrom, filters.dateTo],
  );

  return { filters, apiRange, setRange, setGroupId, setShowDeleted };
};
